import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CoachLocation, PublicBookingPage as PublicBookingPagePayload, createPublicIntroBooking, getPublicBookingPage } from '../../api/client';
import { ArrowLeft, Building2, Calendar, CheckCircle2, Clock3, Loader2, MapPin } from 'lucide-react';

export function PublicBookingPage() {
  const { publicSlug } = useParams<{ publicSlug: string }>();
  const [page, setPage] = useState<PublicBookingPagePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ manageUrl: string; startLocal: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedStartAt, setSelectedStartAt] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    if (!publicSlug) {
      return;
    }

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const payload = await getPublicBookingPage(publicSlug!);
        setPage(payload);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load booking page');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [publicSlug]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!publicSlug || !selectedStartAt) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await createPublicIntroBooking(publicSlug!, {
        ...form,
        startAt: selectedStartAt,
      });
      setSuccess({
        manageUrl: result.manageUrl,
        startLocal: result.booking.startLocal,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to book intro session');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  if (error && !page) {
    return (
      <PublicPageShell>
        <ErrorCard title="Booking page unavailable" message={error} />
      </PublicPageShell>
    );
  }

  if (!page) {
    return null;
  }

  return (
    <PublicPageShell>
      <div className="space-y-8">
        <div className="app-panel overflow-hidden">
          <div
            className="px-6 py-8 md:px-10"
            style={{ background: 'linear-gradient(180deg, var(--color-brand-faint), transparent)' }}
          >
            <Link to="/" className="app-link inline-flex items-center gap-2 text-sm font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back to TrackWithAI
            </Link>
            <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent">First Session Free</p>
                <h1 className="display-serif mt-3 text-4xl md:text-5xl text-ink">{page.coach.displayName}</h1>
                <p className="mt-3 text-base text-ink-soft max-w-2xl">{page.offer.description}</p>
              </div>
              <LocationSummary location={page.location} />
            </div>
          </div>
        </div>

        {success ? (
          <div className="rounded-panel border border-success-soft bg-success-soft p-8">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-success mt-0.5" />
              <div>
                <h2 className="display-serif text-2xl text-ink">Intro session booked</h2>
                <p className="mt-2 text-ink-soft">You’re confirmed for {success.startLocal}.</p>
                <p className="mt-2 text-sm text-muted break-all">Manage link: {success.manageUrl}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a
                    href={success.manageUrl}
                    className="app-button app-button--primary app-button--compact"
                  >
                    Manage Booking
                  </a>
                  <Link
                    to="/login"
                    className="app-button app-button--secondary app-button--compact"
                  >
                    Create or sign in to your account
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-8 xl:grid-cols-[1.15fr_0.85fr]">
            <div className="app-panel p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-5 h-5 text-brand" />
                <div>
                  <h2 className="display-serif text-2xl text-ink">Choose a time</h2>
                  <p className="text-sm text-muted mt-1">Book into this building’s open intro-session schedule.</p>
                </div>
              </div>
              {page.slots.length === 0 ? (
                <ErrorCard title="No intro slots yet" message="This coach has not opened public intro availability for this building yet." />
              ) : (
                <div className="grid gap-3">
                  {page.slots.map((slot) => (
                    <label
                      key={slot.startAt}
                      className={`rounded-card border p-4 cursor-pointer transition-colors ${selectedStartAt === slot.startAt ? 'border-brand bg-brand-faint' : 'border-line bg-surface-strong hover:border-line-strong'}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="booking-slot"
                          className="mt-1"
                          style={{ accentColor: 'var(--color-brand)' }}
                          checked={selectedStartAt === slot.startAt}
                          onChange={() => setSelectedStartAt(slot.startAt)}
                        />
                        <div>
                          <p className="font-medium text-ink">{slot.dayLabel}</p>
                          <p className="text-sm text-ink-soft mt-1">{slot.timeLabel}</p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="app-panel p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <Building2 className="w-5 h-5 text-brand" />
                <div>
                  <h2 className="display-serif text-2xl text-ink">Reserve your intro</h2>
                  <p className="text-sm text-muted mt-1">No sign-in required for the first free session.</p>
                </div>
              </div>
              {error && (
                <div className="error-banner mb-4">
                  {error}
                </div>
              )}
              <form className="space-y-4" onSubmit={handleSubmit}>
                <BookingField label="Full Name">
                  <input
                    className={inputClassName}
                    value={form.name}
                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    placeholder="Jordan Harper"
                  />
                </BookingField>
                <BookingField label="Email">
                  <input
                    className={inputClassName}
                    type="email"
                    value={form.email}
                    onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                    placeholder="jordan@example.com"
                  />
                </BookingField>
                <BookingField label="Phone">
                  <input
                    className={inputClassName}
                    value={form.phone}
                    onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                    placeholder="(917) 555-0188"
                  />
                </BookingField>
                <div className="rounded-card border border-line bg-surface-muted p-4">
                  <p className="text-sm font-medium text-ink">What happens next</p>
                  <ul className="mt-3 space-y-2 text-sm text-ink-soft">
                    <li>Your intro session is booked instantly if the slot is still open.</li>
                    <li>You’ll get a confirmation email with a link to manage your booking.</li>
                    <li>You can cancel or reschedule later from the magic link in your confirmation.</li>
                  </ul>
                </div>
                <button
                  type="submit"
                  disabled={submitting || !selectedStartAt}
                  className="app-button app-button--primary app-button--block"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Clock3 className="w-5 h-5" />}
                  Book Free Intro
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </PublicPageShell>
  );
}

function PublicPageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-app-bg text-ink">
      <div className="max-w-6xl mx-auto px-4 py-8 md:px-6 md:py-12">
        {children}
      </div>
    </div>
  );
}

function LocationSummary({ location }: { location: CoachLocation }) {
  return (
    <div className="rounded-card border border-line bg-surface-strong shadow-card px-5 py-4 max-w-md">
      <div className="flex items-center gap-2 text-sm font-medium text-ink">
        <Building2 className="w-4 h-4 text-brand" />
        {location.name}
      </div>
      <div className="mt-3 flex items-start gap-2 text-sm text-ink-soft">
        <MapPin className="w-4 h-4 mt-0.5 text-muted" />
        <span>{location.address}</span>
      </div>
      <div className="mt-3 flex items-center gap-2 text-sm text-ink-soft">
        <Clock3 className="w-4 h-4 text-muted" />
        <span>{location.timezone}</span>
      </div>
    </div>
  );
}

function BookingField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink-soft mb-2">{label}</span>
      {children}
    </label>
  );
}

function ErrorCard({ title, message }: { title: string; message: string }) {
  return (
    <div className="app-panel border-danger-soft p-8">
      <h2 className="display-serif text-2xl text-ink">{title}</h2>
      <p className="mt-3 text-muted">{message}</p>
    </div>
  );
}

const inputClassName = 'app-input';
